<!-- Organization Switcher Component -->
<!-- System Admin Mode - No organization context needed -->
@if (user && user.isSystemAdmin) {
  <div class="organization-switcher" data-testid="org-switcher-admin-badge">
    <div class="flex items-center gap-2 px-3 py-2 bg-error/10 border border-error/20 rounded-lg">
      <app-icon name="admin_panel_settings" size="sm" class="text-error"></app-icon>
      <span class="font-semibold text-error">System Admin Mode</span>
    </div>
  </div>
}

<!-- Regular Users - Show organization switcher -->
@if (user && !user.isSystemAdmin && user.organizations && user.organizations.length > 0) {
  <div class="organization-switcher">
    <p class="text-xs uppercase tracking-widest text-base-content/40 font-semibold px-3 mb-1">Active Workspace</p>
    <!-- Single Organization Display (no dropdown) -->
    @if (user.organizations.length === 1) {
      <div
        class="flex items-center gap-2 px-3 py-2 bg-base-200/50 rounded-lg w-full"
        data-testid="org-switcher-single-org">
        <app-icon name="corporate_fare" size="sm" class="text-primary"></app-icon>
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium flex gap-1" [class.truncate]="!wrapName" [class.items-center]="!wrapName" [class.items-baseline]="wrapName" [title]="user.organizations[0].name">
            <span
              class="min-w-0"
              [class.truncate]="!wrapName"
              [class.user-text-wrap]="wrapName"
              data-testid="org-switcher-current-name">{{ user.organizations[0].name }}</span>
            @if (user.organizations[0].isDefault) {
              <span class="badge badge-xs badge-primary badge-outline flex-shrink-0">Default</span>
            }
          </div>
          <span class="badge badge-xs" [ngClass]="getRoleBadgeClass(user.organizations[0].role)">
            {{ getRoleDisplay(user.organizations[0].role) }}
          </span>
        </div>
      </div>
    }
    <!-- Multiple Organizations Dropdown -->
    @if (user.organizations.length > 1) {
      <div class="relative w-full">
        <div
          role="button"
          class="flex items-center gap-2 px-3 py-2 bg-base-200/50 rounded-lg cursor-pointer hover:bg-base-200 transition-colors w-full"
          (click)="toggleDropdown()"
          [class.opacity-50]="isLoading"
          [attr.aria-expanded]="isDropdownOpen"
          aria-haspopup="listbox"
          aria-label="Switch organization"
          data-testid="org-switcher-toggle-btn">
          <!-- Loading spinner -->
          @if (isLoading) {
            <div class="flex items-center gap-2 flex-1 min-w-0">
              <span class="loading loading-spinner loading-xs"></span>
              <span class="text-sm">Loading...</span>
            </div>
          }
          <!-- Current organization -->
          @if (!isLoading && currentOrg) {
            <div class="flex items-center gap-2 flex-1 min-w-0">
              <app-icon name="corporate_fare" size="sm" class="text-primary"></app-icon>
              <div class="flex-1 min-w-0">
                <!--
                  KAN-624. `nameOverflow="wrap"` (the top-bar copy) drops both
                  `truncate`s so the whole name shows, wrapping onto more lines if it
                  must. `user-text-wrap` (the KAN-211 Pattern B utility) breaks a name
                  with no usable spaces instead of letting it push the "Default" badge
                  and the chevron out of the picker.
                -->
                <div class="text-sm font-medium flex gap-1" [class.truncate]="!wrapName" [class.items-center]="!wrapName" [class.items-baseline]="wrapName" [title]="currentOrg.name">
                  <span
                    class="min-w-0"
                    [class.truncate]="!wrapName"
                    [class.user-text-wrap]="wrapName"
                    data-testid="org-switcher-current-name">{{ currentOrg.name }}</span>
                  @if (currentOrg.isDefault) {
                    <span class="badge badge-xs badge-primary badge-outline flex-shrink-0">Default</span>
                  }
                </div>
                <span class="badge badge-xs" [ngClass]="getRoleBadgeClass(currentOrg.role)">
                  {{ getRoleDisplay(currentOrg.role) }}
                </span>
              </div>
            </div>
          }
          <!-- No org selected -->
          @if (!isLoading && !currentOrg) {
            <div class="flex items-center gap-2 flex-1 min-w-0">
              <app-icon name="corporate_fare" size="sm" class="text-base-content/60"></app-icon>
              <span class="text-sm">Select Organization</span>
            </div>
          }
          <!-- Dropdown icon -->
          <app-icon name="expand_more"
            size="sm"
            class="text-base-content/60 transition-transform duration-200"
          [class.rotate-180]="isDropdownOpen"></app-icon>
        </div>
        <!-- Dropdown menu -->
        @if (isDropdownOpen) {
          <!--
            KAN-567. The 320px floor here used to be unconditional, and it is the
            SECOND of two independent overflows on this component - fixing only the
            closed-state one is what sent this card back to Failed QA.

            This list is `absolute` and the toggle starts 152px in from the left on a
            phone, so a flat `min-w-[320px] w-max` ended the menu at 498.5px no matter
            how wide the screen was: at 320 every organization row sat entirely off
            screen, and it was still 84px over at 414 where the CLOSED state is already
            clean. So the floor now applies only from `sm` up, and the menu is capped
            against the viewport instead.

            `menuAlign` exists because this component renders THREE times (desktop
            sidebar, the sidebar inside the collapsed mobile menu, and the top bar). The
            top-bar copy must be RIGHT-anchored or it runs from 88px to 392px on a 320px
            screen; the sidebar copies must stay left-anchored, where flipping them could
            push the menu off the LEFT edge. A bare CSS rule would hit all three, which
            is why this is an input and not a stylesheet change.

            `overflow-x: hidden` is NOT an acceptable alternative: it satisfies a
            scrollWidth assertion while hiding the workspace picker, and clipped overflow
            photographs exactly like correct layout. Guarded by
            e2e/tests/app-shell-horizontal-overflow.spec.ts.
          -->
          <ul
            class="menu bg-base-100 rounded-lg z-[1000] min-w-0 sm:min-w-[320px] w-max max-w-[calc(100vw-1rem)] p-2 shadow-xl border border-base-300 mt-1 absolute top-full"
            [class.left-0]="menuAlign === 'start'"
            [class.right-0]="menuAlign === 'end'"
            role="listbox"
            aria-label="Organizations">
            <li class="menu-title px-3 py-1 min-w-0">
              <span class="text-xs uppercase text-base-content/60 truncate">Switch Organization</span>
            </li>
            <!-- Organization list -->
            @for (org of user.organizations; track org) {
              <li class="min-w-0 w-full">
                <a
                  class="flex items-center justify-between px-3 py-2 rounded-md transition-colors min-w-0 w-full"
                  [class.bg-primary]="org.id === user.currentOrgId"
                  [class.text-primary-content]="org.id === user.currentOrgId"
                  [class.font-semibold]="org.id === user.currentOrgId"
                  [class.hover:bg-base-200]="org.id !== user.currentOrgId"
                  [class.cursor-default]="org.id === user.currentOrgId"
                  [class.cursor-pointer]="org.id !== user.currentOrgId"
                  (click)="switchOrganization(org); $event.stopPropagation()"
                  [attr.data-testid]="'org-switcher-' + org.id + '-item'"
                  [attr.data-teststate]="org.id === user.currentOrgId ? 'active' : 'inactive'">
                  <div class="flex items-center gap-2 flex-1 min-w-0">
                    <app-icon [name]="org.id === user.currentOrgId ? 'check_circle' : 'corporate_fare'" size="sm" class="flex-shrink-0"></app-icon>
                    <span class="truncate min-w-0" [title]="org.name">{{ org.name }}</span>
                    @if (org.isDefault) {
                      <span
                        class="badge badge-xs ml-1 flex-shrink-0"
                        [ngClass]="org.id === user.currentOrgId ? 'bg-white/30 text-primary-content border-0' : 'badge-primary badge-outline'">
                        Default
                      </span>
                    }
                  </div>
                  <span class="badge badge-xs ml-2"
                    [ngClass]="org.id === user.currentOrgId ? 'bg-white/30 text-primary-content border-0' : getRoleBadgeClass(org.role)">
                    {{ getRoleDisplay(org.role) }}
                  </span>
                </a>
              </li>
            }
          </ul>
        }
      </div>
    }
  </div>
}